import React from 'react';
import { motion } from 'framer-motion';
import Section from '../components/Section';
import Stats from '../components/Stats';
import Card from '../components/Card';
import Button from '../components/Button';
import { Quote } from 'lucide-react';
import { useSanityContent } from '../hooks/useSanityContent';
import { fetchTrackRecord } from '../lib/sanity';
import { defaultTrackRecord } from '../lib/defaultContent';
import type { TrackRecordContent } from '../lib/types';

const TrackRecord: React.FC = () => {
  const { content } = useSanityContent<TrackRecordContent>(fetchTrackRecord, defaultTrackRecord);
  const photoRowImages =
    content.photoRowImages && content.photoRowImages.length
      ? content.photoRowImages
      : defaultTrackRecord.photoRowImages;
  const milestonesBg =
    content.milestonesBackgroundImage || defaultTrackRecord.milestonesBackgroundImage;

  return (
    <main className="pt-20">
      <Section padding="default">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-playfair font-bold text-primary-black mb-6">
            {content.headerTitle}{' '}
            <span className="text-primary-red">{content.headerHighlight}</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
            {content.headerDescription}
          </p>
        </motion.div>

        {/* Stats above the video */}
        <div className="mb-12">
          <Stats stats={content.mainStats} className="mb-8" />
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center text-lg text-gray-600 max-w-3xl mx-auto"
          >
            {content.headerNote}
          </motion.p>
        </div>

        {/* Photo row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="flex flex-col sm:flex-row gap-4 mb-20 sm:h-[420px]"
        >
          <div className="h-56 sm:h-full sm:flex-1 rounded-2xl overflow-hidden shadow-md">
            <img
              src={photoRowImages[0]}
              alt="Red sacks on truck"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="h-56 sm:h-full sm:flex-1 rounded-2xl overflow-hidden shadow-md">
            <img
              src={photoRowImages[1]}
              alt="Two trucks on the road"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="h-56 sm:h-full sm:flex-1 rounded-2xl overflow-hidden shadow-md">
            <img
              src={photoRowImages[2]}
              alt="Man on truck"
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {content.additionalMetrics.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="bg-cream-50 rounded-xl p-6 shadow-sm text-center"
            >
              <div className="text-4xl font-playfair font-bold text-primary-red mb-2">
                {item.value}
              </div>
              <div className="text-sm text-gray-600 font-inter">{item.metric}</div>
            </motion.div>
          ))}
        </div>

        <div className="mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-primary-black mb-4">
              {content.testimonialsTitle}
            </h2>
            <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
              {content.testimonialsDescription}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {content.testimonials.map((testimonial, index) => (
              <Card key={index}>
                <Quote className="w-10 h-10 text-primary-red/20 mb-4" />
                <p className="text-gray-700 italic mb-6 leading-relaxed">
                  "{testimonial.quote}"
                </p>
                <div className="mt-auto">
                  <div className="font-playfair font-bold text-primary-black">
                    {testimonial.author}
                  </div>
                  <div className="text-sm text-gray-600">{testimonial.role}</div>
                  <div className="text-sm text-primary-red">{testimonial.company}</div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Key Milestones — dark grain-image background */}
        <div className="mb-20">
          <div className="relative rounded-2xl overflow-hidden">
            {/* Background image */}
            <img
              src={milestonesBg}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-primary-black/80" />

            {/* Content */}
            <div className="relative z-10 px-8 md:px-16 py-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mb-12"
              >
                <h2 className="text-4xl md:text-5xl font-playfair font-bold text-white mb-3">
                  {content.timelineTitle}
                </h2>
                <p className="text-white/60 text-lg">{content.timelineSubtitle}</p>
              </motion.div>

              <div className="space-y-6">
                {content.milestones.map((milestone, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.08 }}
                    className="flex gap-6 items-start"
                  >
                    <div className="flex-shrink-0 w-20 h-20 rounded-full bg-primary-red text-white flex items-center justify-center font-playfair font-bold text-xl">
                      {milestone.year}
                    </div>
                    <div className="flex-grow bg-white/10 backdrop-blur-sm border border-white/10 rounded-xl p-5">
                      <h3 className="text-xl font-playfair font-bold text-white mb-1">
                        {milestone.title}
                      </h3>
                      <p className="text-white/70 text-sm leading-relaxed">{milestone.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-br from-primary-red/10 to-secondary-terracotta/10 rounded-2xl p-8 md:p-12 text-center"
        >
          <h3 className="text-3xl md:text-4xl font-playfair font-bold text-primary-black mb-6">
            {content.ctaTitle}
          </h3>
          <p className="text-lg md:text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
            {content.ctaDescription}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="primary" href={content.ctaPrimaryLink}>
              {content.ctaPrimaryText}
            </Button>
            <Button variant="secondary" href={content.ctaSecondaryLink}>
              {content.ctaSecondaryText}
            </Button>
          </div>
        </motion.div>
      </Section>
    </main>
  );
};

export default TrackRecord;
